import { ChangeDetectorRef, Component, effect, inject, input, output, signal, untracked, viewChild } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { Tab, TabList, Tabs } from 'primeng/tabs';
import { Skeleton } from 'primeng/skeleton';
import { Chip } from 'primeng/chip';
import { Dialog } from 'primeng/dialog';
import { Paginator } from 'primeng/paginator';
import { Menu } from 'primeng/menu';
import { MenuItem } from 'primeng/api';

import {
  AppointmentsClient,
  AppointmentStatus,
  ClientAppointmentTableResponse,
  GetClientAppointmentsQuery,
  IPaginationResponseOfClientAppointmentTableResponse,
} from '../../model/fss.api';
import {
  ButtonComponent,
  ButtonSeverity,
  ButtonSize,
} from '../../ui/button/button';
import { Arrow } from '../../icons/arrow/arrow';
import { CalendarCheck01 } from '../../icons/calendar-check-01/calendar-check-01';
import { ClockRewind } from '../../icons/clock-rewind/clock-rewind';
import { Edit03 } from '../../icons/edit-03/edit-03';
import { SlashCircle01 } from '../../icons/slash-circle-01/slash-circle-01';
import { XClose } from '../../icons/x-close/x-close';
import { AlertTriangle } from '../../icons/alert-triangle/alert-triangle';
import { convertDateToHhMm, getMonthShortSr } from '../../services/date.service';
import { AlertService } from '../../services/alert.service';
import { LoaderService } from '../loader/loader.service';
import { AppointmentRescheduleDialog } from '../../../../app/admin/admin-appointments-table/appointment-reschedule-dialog/appointment-reschedule-dialog';
import { AppointmentDetailsDrawer } from '../../../../app/admin/admin-appointments-table/appointment-details-drawer/appointment-details-drawer';
import { ClientAppointmentRescheduleDialog } from '../client-appointment-reschedule-dialog/client-appointment-reschedule-dialog';
import { ClientAppointmentCancelDialog } from '../client-appointment-cancel-dialog/client-appointment-cancel-dialog';
import { ClientAppointmentDetailsDrawer } from '../client-appointment-details-drawer/client-appointment-details-drawer';
import { InfoCircle } from '../../icons/info-circle/info-circle';
import { Clock } from '../../icons/clock/clock';
import { ClientPackageStateService } from '../../services/client-package-state.service';
import { UserService } from '../../services/user.service';
import { LocationCompanyService } from '../../services/location-company.service';
import { LocationIndividualService } from '../../services/location-individual.service';

type SubTab = 0 | 1; // 0 = Predstojeći, 1 = Istorija

@Component({
  standalone: true,
  selector: 'app-client-appointments-table',
  imports: [
    Tab,
    TabList,
    Tabs,
    Skeleton,
    Chip,
    Dialog,
    Paginator,
    Menu,
    ButtonComponent,
    Arrow,
    CalendarCheck01,
    ClockRewind,
    Edit03,
    SlashCircle01,
    XClose,
    AlertTriangle,
    AppointmentRescheduleDialog,
    AppointmentDetailsDrawer,
    ClientAppointmentRescheduleDialog,
    ClientAppointmentCancelDialog,
    ClientAppointmentDetailsDrawer,
    InfoCircle,
    Clock,
  ],
  templateUrl: './client-appointments-table.html',
  styleUrl: './client-appointments-table.scss',
})
export class ClientAppointmentsTable {
  private _client = inject(AppointmentsClient);
  private _cdr = inject(ChangeDetectorRef);
  private _alert = inject(AlertService);
  private _loader = inject(LoaderService);
  private _user = inject(UserService);
  private _companyLocation = inject(LocationCompanyService);
  private _individualLocation = inject(LocationIndividualService);
  protected packageState = inject(ClientPackageStateService);

  userId = input.required<string>();
  isClientView = input<boolean>(false);

  changed = output<void>();

  appointments: ClientAppointmentTableResponse[] = [];
  isLoading = true;
  activeTab = signal<SubTab>(0);
  first = 0;

  paginatedData: IPaginationResponseOfClientAppointmentTableResponse = {
    currentPage: 1,
    pageSize: 10,
    totalCount: 0,
  } as IPaginationResponseOfClientAppointmentTableResponse;

  /** Reschedule dialog */
  showRescheduleDialog = signal(false);
  selectedRescheduleId = signal<string | undefined>(undefined);

  /** Cancel confirm dialog */
  showCancelDialog = signal(false);
  selectedCancelId = signal<string | undefined>(undefined);

  /** Details drawer */
  showDetailsDrawer = signal(false);
  selectedDetailsId = signal<string | undefined>(undefined);

  rowMenu = viewChild<Menu>('rowMenu');
  rowMenuItems: MenuItem[] = [];

  openRowMenu(appt: ClientAppointmentTableResponse, event: MouseEvent) {
    event.stopPropagation();
    const items: MenuItem[] = [];
    if (!appt.isShortNotice) {
      items.push({
        label: 'Izmeni',
        icon: 'pi pi-pencil',
        command: () => this.openRescheduleFromMenu(appt.id!),
      });
    }
    items.push({
      label: 'Otkaži',
      icon: 'pi pi-ban',
      command: () => this.openCancelFromMenu(appt.id!),
    });
    this.rowMenuItems = items;
    this.rowMenu()?.toggle(event);
  }

  private openRescheduleFromMenu(id: string) {
    this.selectedRescheduleId.set(id);
    this.showRescheduleDialog.set(true);
  }

  private openCancelFromMenu(id: string) {
    this.selectedCancelId.set(id);
    this.showCancelDialog.set(true);
  }

  openDetails(id: string) {
    this.selectedDetailsId.set(id);
    this.showDetailsDrawer.set(true);
  }

  goToCreateAppointment() {
    if (this._user.isCompany()) {
      this._companyLocation.routeToCreateAppointment();
    } else {
      this._individualLocation.routeToCreateAppointment();
    }
  }

  onDetailsAction() {
    this.load();
    this.changed.emit();
  }

  constructor() {
    effect(() => {
      const id = this.userId();
      if (id) {
        untracked(() => this.load());
      }
    });
  }

  onTabChange(value: any) {
    this.activeTab.set(Number(value ?? 0) as SubTab);
    this.paginatedData.currentPage = 1;
    this.load();
  }

  load() {
    this.isLoading = true;
    this._cdr.detectChanges();

    this._loadAppointments().then((res) => {
      this.appointments = res.result.data ?? [];
      this.paginatedData = res.result;
      this.first = (this.paginatedData.currentPage! - 1) * this.paginatedData.pageSize!;
      this.isLoading = false;
      this._cdr.markForCheck();
    });
  }

  onPageChange(event: any) {
    this.paginatedData.pageSize = event.rows;
    this.paginatedData.currentPage = Math.floor(event.first / event.rows) + 1;
    this.load();
  }

  next() {
    this.paginatedData.currentPage!++;
    this.load();
  }

  prev() {
    this.paginatedData.currentPage!--;
    this.load();
  }

  openRescheduleDialog(id: string, event: Event) {
    event.stopPropagation();
    this.selectedRescheduleId.set(id);
    this.showRescheduleDialog.set(true);
  }

  onRescheduled() {
    this.load();
    this.changed.emit();
  }

  openCancelDialog(id: string, event: Event) {
    event.stopPropagation();
    this.selectedCancelId.set(id);
    this.showCancelDialog.set(true);
  }

  closeCancelDialog() {
    this.showCancelDialog.set(false);
    this.selectedCancelId.set(undefined);
  }

  onCancelled() {
    this.selectedCancelId.set(undefined);
    this.load();
    this.changed.emit();
  }

  confirmCancel() {
    const id = this.selectedCancelId();
    if (!id) return;
    this._loader.setLoadingText = 'Otkazivanje...';
    this._loader.setShowLoader = true;
    this._cancel(id).then((res) => {
      this.closeCancelDialog();
      this.load();
      this.changed.emit();
      this._alert.addSuccessMsg(res.result);
    });
  }

  getStatusLabel(a: ClientAppointmentTableResponse): string {
    if (this.activeTab() === 0) return 'Zakazan';
    if (a.status?.value === AppointmentStatus.CANCELED) return 'Otkazan';
    if (a.status?.value === AppointmentStatus.NO_SHOW) return 'Nije se pojavio';
    return 'Završen';
  }

  getStatusClass(a: ClientAppointmentTableResponse): string {
    if (this.activeTab() === 0) return 'info sm';
    if (a.status?.value === AppointmentStatus.CANCELED) return 'red sm';
    if (a.status?.value === AppointmentStatus.NO_SHOW) return 'warn sm';
    return 'gray sm';
  }

  formatTimeRange(from?: Date, to?: Date): string {
    if (!from || !to) return '';
    return `${convertDateToHhMm(from)} - ${convertDateToHhMm(to)}`;
  }

  getMonthLabel(date?: Date): string {
    if (!date) return '';
    return getMonthShortSr(date);
  }

  getDayLabel(date?: Date): string {
    if (!date) return '';
    return String(date.getDate()).padStart(2, '0');
  }

  private async _loadAppointments() {
    return await firstValueFrom(
      this._client.getClientAppointments({
        userId: this.userId(),
        isUpcoming: this.activeTab() === 0,
        pageNumber: this.paginatedData.currentPage,
        pageSize: this.paginatedData.pageSize,
      } as GetClientAppointmentsQuery),
    );
  }

  private async _cancel(id: string) {
    return await firstValueFrom(this._client.cancelAppointment(id));
  }

  protected readonly ButtonSeverity = ButtonSeverity;
  protected readonly ButtonSize = ButtonSize;
}
